import React, { useEffect, useState } from "react";
import {
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Field, Form, Formik } from "formik";
import EditIcon from "@material-ui/icons/Edit";
import { toast } from "react-toastify";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";
import usePlans from "../../hooks/usePlans";
import { i18n } from "../../translate/i18n";
import { BooleanBadge, SectionCard, SettingSwitch } from "../Settings/AdminUI";

const defaultPlan = {
  id: undefined,
  name: "",
  users: 0,
  connections: 0,
  queues: 0,
  amount: 0,
  useWhatsapp: true,
  useFacebook: true,
  useInstagram: true,
  useCampaigns: true,
  useSchedules: true,
  useInternalChat: true,
  useExternalApi: true,
  useKanban: true,
  useOpenAi: true,
  useIntegrations: true,
  isPublic: true,
  useWhatsappOfficial: false,
  trial: false,
  trialDays: 3,
  recurrence: "monthly",
  wavoip: false,
};

const featureFields = [
  ["useWhatsapp", "Whatsapp"],
  ["useWhatsappOfficial", "Whatsapp Oficial"],
  ["useFacebook", "Facebook"],
  ["useInstagram", "Instagram"],
  ["useCampaigns", "Campanhas"],
  ["useSchedules", "Agendamentos"],
  ["useInternalChat", "Chat Interno"],
  ["useExternalApi", "API Externa"],
  ["useKanban", "Kanban"],
  ["useOpenAi", "Talk.Ai"],
  ["useIntegrations", "Integrações"],
  ["wavoip", "Wavoip"],
];

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  mainPaper: {
    width: "100%",
    flex: 1,
    background: "transparent",
  },
  fullWidth: {
    width: "100%",
  },
  formStack: {
    display: "grid",
    gap: theme.spacing(2.5),
  },
  modernInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      background: "#fafbfc",
      minHeight: 48,
      "& fieldset": {
        borderColor: "#e6eaf0",
      },
      "&:hover fieldset": {
        borderColor: "#cfd6e0",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        boxShadow: "0 0 0 3px rgba(63, 81, 181, 0.08)",
      },
    },
  },
  featureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
    gap: theme.spacing(1.5),
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  tableContainer: {
    width: "100%",
    overflowX: "auto",
    borderRadius: 14,
    border: "1px solid #edf0f5",
    boxShadow: "none",
    ...theme.scrollbarStyles,
  },
  tableHead: {
    backgroundColor: "#f8fafc",
    "& .MuiTableCell-head": {
      color: "#475569",
      fontWeight: 800,
      whiteSpace: "nowrap",
      padding: theme.spacing(1.5),
    },
  },
  tableCell: {
    padding: theme.spacing(1.25, 1.5),
    whiteSpace: "nowrap",
    borderBottom: "1px solid #eef2f7",
  },
  formSection: {
    marginTop: theme.spacing(2.5),
    paddingTop: theme.spacing(2.5),
    borderTop: "1px solid #eef2f7",
    "&:first-of-type": {
      marginTop: 0,
      paddingTop: 0,
      borderTop: 0,
    },
  },
  sectionLabel: {
    marginBottom: theme.spacing(1),
    color: "#687182",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
}));

export function PlanManagerForm({ onSubmit, onDelete, onCancel, initialValue, loading }) {
  const classes = useStyles();
  const [record, setRecord] = useState(defaultPlan);

  useEffect(() => {
    setRecord({ ...defaultPlan, ...initialValue });
  }, [initialValue]);

  const SwitchField = ({ name, label }) => (
    <Field name={name}>
      {({ field, form }) => (
        <SettingSwitch
          label={label}
          checked={field.value === false ? false : true}
          onChange={(checked) => form.setFieldValue(name, checked)}
        />
      )}
    </Field>
  );

  return (
    <Formik
      enableReinitialize
      initialValues={record}
      onSubmit={(values, { resetForm }) =>
        setTimeout(() => {
          onSubmit(values);
          resetForm();
        }, 500)
      }
    >
      {({ values }) => (
        <Form className={classes.fullWidth}>
          <div className={classes.formStack}>
            <SectionCard
              title={record.id ? "Editar plano" : "Cadastro de plano"}
              description="Dados comerciais, limites e recursos em uma visão única."
            >
              <div className={classes.formSection}>
                <div className={classes.sectionLabel}>Dados do plano</div>
              <Grid spacing={2} container>
                <Grid xs={12} sm={6} md={5} item>
                  <Field as={TextField} label={i18n.t("plans.form.name")} name="name" variant="outlined" className={`${classes.fullWidth} ${classes.modernInput}`} margin="dense" />
                </Grid>
                <Grid xs={12} sm={6} md={3} item>
                  <Field as={TextField} label="Valor" name="amount" variant="outlined" className={`${classes.fullWidth} ${classes.modernInput}`} margin="dense" />
                </Grid>
                <Grid xs={12} sm={6} md={2} item>
                  <FormControl margin="dense" variant="outlined" fullWidth className={classes.modernInput}>
                    <InputLabel>Plano público</InputLabel>
                    <Field as={Select} label="Plano público" name="isPublic">
                      <MenuItem value={true}>Sim</MenuItem>
                      <MenuItem value={false}>Não</MenuItem>
                    </Field>
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6} md={2} item>
                  <FormControl margin="dense" variant="outlined" fullWidth className={classes.modernInput}>
                    <InputLabel>Trial</InputLabel>
                    <Field as={Select} label="Trial" name="trial">
                      <MenuItem value={true}>Sim</MenuItem>
                      <MenuItem value={false}>Não</MenuItem>
                    </Field>
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6} md={2} item>
                  <Field as={TextField} label={i18n.t("plans.form.trialDays")} name="trialDays" variant="outlined" className={`${classes.fullWidth} ${classes.modernInput}`} margin="dense" type="number" inputProps={{ min: 1 }} disabled={!values.trial} />
                </Grid>
              </Grid>
              </div>

              <div className={classes.formSection}>
                <div className={classes.sectionLabel}>Limites</div>
              <Grid spacing={2} container>
                <Grid xs={12} sm={4} item>
                  <Field as={TextField} label={i18n.t("plans.form.users")} name="users" variant="outlined" className={`${classes.fullWidth} ${classes.modernInput}`} margin="dense" type="number" />
                </Grid>
                <Grid xs={12} sm={4} item>
                  <Field as={TextField} label={i18n.t("plans.form.connections")} name="connections" variant="outlined" className={`${classes.fullWidth} ${classes.modernInput}`} margin="dense" type="number" />
                </Grid>
                <Grid xs={12} sm={4} item>
                  <Field as={TextField} label="Filas" name="queues" variant="outlined" className={`${classes.fullWidth} ${classes.modernInput}`} margin="dense" type="number" />
                </Grid>
              </Grid>
              </div>

              <div className={classes.formSection}>
                <div className={classes.sectionLabel}>Canais e recursos</div>
              <div className={classes.featureGrid}>
                {featureFields.map(([name, label]) => (
                  <SwitchField key={name} name={name} label={label} />
                ))}
              </div>
              </div>
            </SectionCard>

            <div className={classes.actions}>
              <ButtonWithSpinner loading={loading} onClick={() => onCancel()} variant="contained">
                {i18n.t("plans.form.clear")}
              </ButtonWithSpinner>
              {record.id !== undefined ? (
                <ButtonWithSpinner loading={loading} onClick={() => onDelete(record)} variant="contained" color="secondary">
                  {i18n.t("plans.form.delete")}
                </ButtonWithSpinner>
              ) : null}
              <ButtonWithSpinner loading={loading} type="submit" variant="contained" color="primary">
                {i18n.t("plans.form.save")}
              </ButtonWithSpinner>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}

export function PlansManagerGrid({ records, onSelect }) {
  const classes = useStyles();

  return (
    <SectionCard title="Planos cadastrados" description="Recursos, limites e valores dos planos disponíveis.">
      <Paper className={classes.tableContainer} elevation={0}>
        <Table className={classes.fullWidth} padding="none" aria-label="plans table">
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell align="center" style={{ width: "1%" }}>#</TableCell>
              <TableCell align="left">{i18n.t("plans.form.name")}</TableCell>
              <TableCell align="center">{i18n.t("plans.form.trial")}</TableCell>
              <TableCell align="center">{i18n.t("plans.form.users")}</TableCell>
              <TableCell align="center">{i18n.t("plans.form.connections")}</TableCell>
              <TableCell align="center">Filas</TableCell>
              <TableCell align="center">Valor</TableCell>
              <TableCell align="center">Público</TableCell>
              {featureFields.map(([, label]) => (
                <TableCell key={label} align="center">{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {records.map((row) => (
              <TableRow key={row.id}>
                <TableCell className={classes.tableCell} align="center">
                  <Tooltip title="Editar plano">
                    <IconButton size="small" onClick={() => onSelect(row)} aria-label="editar">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
                <TableCell className={classes.tableCell} align="left">{row.name || "-"}</TableCell>
                <TableCell className={classes.tableCell} align="center">{row.trial ? `${row.trialDays || 0} dias` : "-"}</TableCell>
                <TableCell className={classes.tableCell} align="center">{row.users || "-"}</TableCell>
                <TableCell className={classes.tableCell} align="center">{row.connections || "-"}</TableCell>
                <TableCell className={classes.tableCell} align="center">{row.queues || "-"}</TableCell>
                <TableCell className={classes.tableCell} align="center">{i18n.t("plans.form.money")} {row.amount ? row.amount.toLocaleString("pt-br", { minimumFractionDigits: 2 }) : "00.00"}</TableCell>
                <TableCell className={classes.tableCell} align="center"><BooleanBadge value={row.isPublic} /></TableCell>
                {featureFields.map(([name]) => (
                  <TableCell key={name} className={classes.tableCell} align="center">
                    <BooleanBadge value={row[name]} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </SectionCard>
  );
}

export default function PlansManager() {
  const classes = useStyles();
  const { list, save, update, remove } = usePlans();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState(defaultPlan);

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const planList = await list();
      setRecords(planList);
    } catch (e) {
      toast.error("Não foi possível carregar a lista de registros");
    }
    setLoading(false);
  };

  const handleSubmit = async (data) => {
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(data);
      } else {
        await save(data);
      }
      await loadPlans();
      handleCancel();
      toast.success("Operação realizada com sucesso!");
    } catch (e) {
      toast.error("Não foi possível realizar a operação. Verifique se já existe uma plano com o mesmo nome ou se os campos foram preenchidos corretamente");
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      await loadPlans();
      handleCancel();
      toast.success("Operação realizada com sucesso!");
    } catch (e) {
      toast.error("Não foi possível realizar a operação");
    }
    setLoading(false);
  };

  const handleCancel = () => {
    setRecord(defaultPlan);
  };

  const handleSelect = (data) => {
    setRecord({
      ...defaultPlan,
      id: data.id,
      name: data.name || "",
      users: data.users || 0,
      connections: data.connections || 0,
      queues: data.queues || 0,
      amount: data.amount?.toLocaleString("pt-br", { minimumFractionDigits: 2 }) || 0,
      useWhatsapp: data.useWhatsapp === false ? false : true,
      useWhatsappOfficial: data.useWhatsappOfficial === false ? false : true,
      useFacebook: data.useFacebook === false ? false : true,
      useInstagram: data.useInstagram === false ? false : true,
      useCampaigns: data.useCampaigns === false ? false : true,
      useSchedules: data.useSchedules === false ? false : true,
      useInternalChat: data.useInternalChat === false ? false : true,
      useExternalApi: data.useExternalApi === false ? false : true,
      useKanban: data.useKanban === false ? false : true,
      useOpenAi: data.useOpenAi === false ? false : true,
      useIntegrations: data.useIntegrations === false ? false : true,
      isPublic: data.isPublic,
      trial: data.trial,
      trialDays: data.trialDays,
      recurrence: data.recurrence,
      wavoip: data.wavoip === false ? false : true,
    });
  };

  return (
    <Paper className={classes.mainPaper} elevation={0}>
      <Grid spacing={3} container>
        <Grid xs={12} item>
          <PlanManagerForm
            initialValue={record}
            onDelete={() => setShowConfirmDialog(true)}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
          />
        </Grid>
        <Grid xs={12} item>
          <PlansManagerGrid records={records} onSelect={handleSelect} />
        </Grid>
      </Grid>
      <ConfirmationModal
        title="Exclusão de Registro"
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => handleDelete()}
      >
        Deseja realmente excluir esse registro?
      </ConfirmationModal>
    </Paper>
  );
}
